import { Component, OnInit, OnDestroy, NgZone, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { AnimationOptions } from 'ngx-lottie';
import { AnimationItem } from 'lottie-web';
import { NotificationService, NotificationItem, NotificationType } from '../../services/notification.service';
import { AnimationService } from '../../services/animation.service';

@Component({
  selector: 'app-notification',
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class NotificationComponent implements OnInit, OnDestroy {
  notifications: NotificationItem[] = [];
  private subscription!: Subscription;
  private timers = new Map<number, ReturnType<typeof setTimeout>>();

  constructor(
    private notificationService: NotificationService,
    private animationService: AnimationService,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.subscription = this.notificationService.notifications$.subscribe(items => {
      const newItems = items.filter(i => !this.timers.has(i.id) && i.autoClose);
      this.notifications = items;

      for (const item of newItems) {
        if (item.autoClose) {
          this.scheduleAutoClose(item);
        }
        if (item.confetti) {
          this.animationService.fireConfetti({ particleCount: 30, spread: 50, origin: { y: 0.7 } });
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.timers.forEach(t => clearTimeout(t));
  }

  get toasts(): NotificationItem[] {
    return this.notifications.filter(n => n.mode === 'toast');
  }

  get modals(): NotificationItem[] {
    return this.notifications.filter(n => n.mode === 'alert' || n.mode === 'confirm');
  }

  getIconClass(type: NotificationType): string {
    switch (type) {
      case 'success': return 'icon-success';
      case 'error': return 'icon-error';
      case 'warning': return 'icon-warning';
      case 'info': return 'icon-info';
    }
  }

  getIconSvg(type: NotificationType): string {
    switch (type) {
      case 'success': return 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z';
      case 'error': return 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z';
      case 'warning': return 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z';
      case 'info': return 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
    }
  }

  getLottieOptions(): AnimationOptions {
    return this.animationService.playSuccessCheck();
  }

  onAnimationCreated(animation: AnimationItem): void {
    this.animationService.onAnimationCreated(animation);
  }

  dismiss(id: number): void {
    this.clearTimer(id);
    this.notificationService.dismiss(id);
  }

  onConfirm(item: NotificationItem): void {
    if (!this.canConfirm(item)) {
      return;
    }
    item.resolve?.(true);
    this.dismiss(item.id);
  }

  canConfirm(item: NotificationItem): boolean {
    if (!item.requireInput) {
      return true;
    }
    return !!(item.inputValue && item.inputValue.trim().length > 0);
  }

  onDeny(item: NotificationItem): void {
    item.resolve?.(false);
    this.dismiss(item.id);
  }

  onBackdropClick(event: MouseEvent, item: NotificationItem): void {
    if ((event.target as HTMLElement).classList.contains('modal-overlay')) {
      if (item.mode === 'alert') {
        this.dismiss(item.id);
      } else if (item.mode === 'confirm') {
        this.onDeny(item);
      }
    }
  }

  trackById(_index: number, item: NotificationItem): number {
    return item.id;
  }

  private scheduleAutoClose(item: NotificationItem): void {
    const timer = setTimeout(() => {
      this.ngZone.run(() => {
        this.dismiss(item.id);
      });
    }, item.autoCloseMs);
    this.timers.set(item.id, timer);
  }

  private clearTimer(id: number): void {
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
  }
}
